import React, { useEffect, useRef, useState } from 'react'
import styled from 'styled-components'
import Modal, { ModalBody, ModalFooter, ModalHeader } from 'core-system/Modal'
import Text from 'core-system/Text'
import PrimaryButton from 'core-system/Button/PrimaryButton'
import palette from 'core-system/Themes/palette'
import pxToRem from 'core-system/utils/pxToRem'

const Form = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  min-width: ${pxToRem(420)};
`

const Description = styled(Text)`
  color: ${palette.text.secondary};
`

const PromptArea = styled.textarea`
  width: 100%;
  min-height: 8rem;
  padding: 0.625rem 1rem;
  border: 1px solid ${palette.grey.grey300};
  border-radius: 0.5rem;
  font: inherit;
  color: ${palette.text.primary};
  resize: vertical;

  &::placeholder {
    color: ${palette.text.placeholder};
  }

  &:focus {
    outline: none;
    border-color: ${palette.text.secondary};
  }

  &:disabled {
    color: ${palette.text.disabled};
    background-color: ${palette.grey.grey200};
  }
`

const ErrorText = styled(Text)`
  color: ${palette.error.red500};
`

const FooterContent = styled.div`
  display: flex;
  justify-content: flex-end;
  width: 100%;
`

interface AiReportModalProps {
  open: boolean
  onClose: () => void
  onSubmit: (prompt: string) => void
  isLoading: boolean
  error: string | null
}

const AiReportModal = ({
  open,
  onClose,
  onSubmit,
  isLoading,
  error,
}: AiReportModalProps) => {
  const [prompt, setPrompt] = useState('')
  const wasLoading = useRef(false)

  useEffect(() => {
    if (wasLoading.current && !isLoading && !error && open) {
      setPrompt('')
      onClose()
    }
    wasLoading.current = isLoading
  }, [isLoading, error, open, onClose])

  useEffect(() => {
    if (!open) {
      setPrompt('')
    }
  }, [open])

  const handleClose = () => {
    if (isLoading) {
      return
    }
    onClose()
  }

  const trimmedPrompt = prompt.trim()
  const isSubmitDisabled = trimmedPrompt.length < 3 || isLoading

  return (
    <Modal open={open} onClose={handleClose} disableEsc={isLoading}>
      <ModalHeader title='AI Report' onClose={handleClose} />
      <ModalBody>
        <Form>
          <Description variant='body1'>
            Describe the report you want. We will query this employer&apos;s
            data and show the results in the table below.
          </Description>
          <PromptArea
            value={prompt}
            onChange={(event) => setPrompt(event.target.value)}
            placeholder='e.g. Employees by worksite with trip counts this year'
            disabled={isLoading}
            aria-label='Report description'
          />
          {error && <ErrorText variant='body2'>{error}</ErrorText>}
        </Form>
      </ModalBody>
      <ModalFooter>
        <FooterContent>
          <PrimaryButton
            onClick={() => onSubmit(trimmedPrompt)}
            disabled={isSubmitDisabled}
            isLoading={isLoading}
          >
            Generate Report
          </PrimaryButton>
        </FooterContent>
      </ModalFooter>
    </Modal>
  )
}

if (process.env.NODE_ENV !== 'production') {
  AiReportModal.displayName = 'AiReportModal'
}

export default AiReportModal
